import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowRight, Smartphone, Gift, ShieldCheck } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const SEEN_KEY = "vt_free_popup_seen";

const POINTS = [
    { icon: Gift, tk: "fsdP1T", dk: "fsdP1D" },
    { icon: Smartphone, tk: "fsdP2T", dk: "fsdP2D" },
    { icon: ShieldCheck, tk: "fsdP3T", dk: "fsdP3D" },
];

export function FreeStartDialog({ open, onOpenChange }) {
    const navigate = useNavigate();
    const { t } = useLang();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-sm rounded-2xl p-5" data-testid="free-start-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[17px] text-[#0F3311] flex items-center gap-2">
                        🎁 {t("fsdTitle")}
                    </DialogTitle>
                    <DialogDescription className="text-[13px] text-[#4A5D4E]">
                        {t("fsdSub")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2.5 mt-1">
                    {POINTS.map((p) => (
                        <div key={p.tk} className="flex items-start gap-2.5">
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#E8F5E9] text-[#1B5E20]">
                                <p.icon className="h-4 w-4" />
                            </span>
                            <div>
                                <p className="text-[12.5px] font-semibold text-[#0F3311]">{t(p.tk)}</p>
                                <p className="text-[11.5px] text-[#4A5D4E] leading-relaxed">{t(p.dk)}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <button
                        data-testid="free-start-continue"
                        onClick={() => { onOpenChange(false); navigate("/signup"); }}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F57C00] hover:bg-[#E65100] text-white rounded-full px-4 py-2.5 text-[13.5px] font-semibold transition-colors"
                    >
                        {t("fsdContinue")} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="free-start-later"
                        onClick={() => onOpenChange(false)}
                        className="inline-flex items-center justify-center rounded-full px-5 py-3 text-[14px] font-medium text-[#1B5E20] border border-[#1B5E20]/30 hover:bg-[#E8F5E9] transition-colors"
                    >
                        {t("fsdLater")}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Auto-open once per browser for first-time visitors. */
export function useFreeStartAutoOpen(setOpen) {
    useEffect(() => {
        try {
            if (localStorage.getItem(SEEN_KEY)) return;
            const t = setTimeout(() => {
                localStorage.setItem(SEEN_KEY, "1");
                setOpen(true);
            }, 1800);
            return () => clearTimeout(t);
        } catch { /* private mode */ }
    }, [setOpen]);
}
